import * as WITConstants from "Presentation/Scripts/TFS/Generated/TFS.WorkItemTracking.Constants";
import { QueryTokenResultModel } from "VSS/Identities/Picker/RestClient";
import * as IdentitiesPickerServices from "VSS/Identities/Picker/Services";
import * as Service from "VSS/Service";
import * as Utils_String from "VSS/Utils/String";
import { LinkTypeName, PageSizes } from "WorkItemTracking/Scripts/OM/WorkItemConstants";
import { IWorkItemData, IWorkItemLinkType, IFieldSetValueOptions } from "WorkItemTracking/Scripts/OM/WorkItemInterfaces";
import { WorkItemManager } from "WorkItemTracking/Scripts/OM/WorkItemManager";
import * as WITResources from "WorkItemTracking/Scripts/Resources/TFS.Resources.WorkItemTracking";
import * as WITOM from "WorkItemTracking/Scripts/TFS.WorkItemTracking";

export interface IWorkItemsImportData {
    columns: string[];
    idsMap: {
        [id: string]: number; // id -> index in fieldValues
    };
    fieldValues: string[][];
    targetIds: number[];
    sourceIds: number[];
    isTreeQuery: boolean;
}

export class QueryResultCSVImporter {
    // bareHeader means a header that has been stripped of its number and lowercased
    private _requiredHeadersMap: { [bareHeader: string]: string };
    private _requiredHeaders: string[] = [];
    private _idHeader: string;
    private _workItemTypeHeader: string;
    private _workItemTypesMap: { [type: string]: WITOM.WorkItemType };

    private c_lineFeedChar = "\n";
    private c_carriageReturnChar = "\r";
    private c_maxWorkItemnsAllowed = 1000;

    private _percentComplete = 0;
    private _lastUpdatePercentComplete = 0;
    private _disposed = false;

    private _identityService: IdentitiesPickerServices.IdentityService;

    constructor(private _store: WITOM.WorkItemStore, private _projectName, private _updatePanelPercentComplete: (number: number) => void) {
        this._requiredHeaders = [WITConstants.CoreFieldRefNames.Title, WITConstants.CoreFieldRefNames.WorkItemType];
        this._requiredHeadersMap = {};
        this._workItemTypesMap = {};
        this._identityService = Service.VssConnection.getConnection().getService(IdentitiesPickerServices.IdentityService);
    }

    public static updateCSVImportData(importData: IWorkItemsImportData, tempId: number, newId: number): void {
        if (tempId >= 0) {
            return;
        }
        importData.idsMap[newId] = importData.idsMap[tempId];
        delete importData.idsMap[tempId];

        const targetIds: number[] = importData.targetIds;
        const sourceIds: number[] = importData.sourceIds;

        for (let i = 0; i < sourceIds.length; i++) {
            if (sourceIds[i] === tempId) {
                sourceIds[i] = newId;
            }
            if (targetIds[i] === tempId) {
                targetIds[i] = newId;
            }
        }
    }

    /*
     * Throws error string if errors have been encountered
     */
    public async importCSVFile(csv: string): Promise<IWorkItemsImportData> {
        this._resetPercentComplete();

        if (Object.keys(this._requiredHeadersMap).length === 0) {
            await this._populateHeaders();
        }
        if (Object.keys(this._workItemTypesMap).length === 0) {
            await this._populateWorkItemTypesMap();
        }

        const contents: string[][] = this._parseCSVFile(csv);
        this._trimContents(contents);
        this._validateHeaders(contents);
        this._validateRequiredFields(contents);

        const csvImportData = this._getCSVImportData(contents);

        if (csvImportData.isTreeQuery) {
            await this._beginGetLinkTypes();
        }

        await this._loadWorkItemsAndSetFieldValues(csvImportData);
        return csvImportData;
    }

    private async _beginGetLinkTypes(): Promise<IWorkItemLinkType[]> {
        return new Promise((resolve, reject) => {
            this._store.beginGetLinkTypes(resolve, error => {
                reject({ message: error.message, errorType: "beginGetLinkTypes" });
            });
        });
    }

    private async _populateHeaders(): Promise<void> {
        // Fields not initialized yet
        await this._store.beginGetFields();

        this._requiredHeaders.forEach(header => {
            const name = this._store.getFieldDefinition(header).name;
            const key = name.toLocaleLowerCase();
            this._requiredHeadersMap[key] = name;
        });
        this._idHeader = this._store.getFieldDefinition(WITConstants.CoreFieldRefNames.Id).name;
        this._workItemTypeHeader = this._store.getFieldDefinition(WITConstants.CoreFieldRefNames.WorkItemType).name;
    }

    private async _populateWorkItemTypesMap(): Promise<void> {
        // Fetch project for available work item types
        const projectPromise: Promise<WITOM.Project> = new Promise((resolve, reject) => {
            this._store.beginGetProject(this._projectName, resolve, error => {
                reject({ message: error.message, errorType: "beginGetProject" });
            });
        });

        const project: WITOM.Project = await projectPromise;

        const workItemTypesPromise: Promise<WITOM.WorkItemType[]> = new Promise((resolve, reject) => {
            project.beginGetWorkItemTypes(project.workItemTypeNames, resolve, error => {
                reject({ message: error.message, errorType: "beginGetWorkItemTypes" });
            });
        });

        const workItemTypes: WITOM.WorkItemType[] = await workItemTypesPromise;

        workItemTypes.forEach(type => {
            this._workItemTypesMap[type.name.toLocaleLowerCase()] = type;
        });
    }

    public dispose(): void {
        this._disposed = true;
    }

    private _updatePercentComplete(percentOfIncrement: number, increment: number, maxPercentComplete: number): void {
        if (this._disposed) {
            return;
        }

        this._percentComplete = Math.min(this._percentComplete + percentOfIncrement * increment, maxPercentComplete);

        // Avoid updating state more than necessary
        if (this._percentComplete - this._lastUpdatePercentComplete >= 0.01) {
            this._lastUpdatePercentComplete = this._percentComplete;
            this._updatePanelPercentComplete(this._percentComplete);
        }
    }

    private _resetPercentComplete(): void {
        this._percentComplete = 0;
        this._lastUpdatePercentComplete = 0;
        this._updatePanelPercentComplete(0);
    }

    private _parseCSVFile(csv: string): string[][] {
        if (csv.length === 0) {
            throw WITResources.ImporCsvEmptyDatasetError;
        }

        // Append on newline because it signals the end of the last row
        if (csv.slice(-1) !== this.c_lineFeedChar) {
            csv += this.c_lineFeedChar;
        }

        const contents = [];
        let currentRow = [];
        let inToken = false;
        let hasQuotes = false;
        let token = "";

        for (let i = 0; i < csv.length; i++) {
            const c = csv[i];
            const isLineFeedEnding = c === this.c_lineFeedChar;
            const isCarriageReturnEnding = c === this.c_carriageReturnChar && i + 1 < csv.length && csv[i + 1] === this.c_lineFeedChar;
            const isLineBreakChar = isLineFeedEnding || isCarriageReturnEnding;

            // Append token
            if ((c === "," && !inToken) || (c === "," && inToken && !hasQuotes)) {
                currentRow.push(token);
                token = "";
                inToken = false;
                hasQuotes = false;
            } else if ((isLineBreakChar && !inToken) || (isLineBreakChar && inToken && !hasQuotes)) {
                // End of the line - append row
                // Flush token, if exists
                if (token !== "") {
                    currentRow.push(token);
                    token = "";
                }

                // Data shape Error: Number of fields in the current row exceeds the number of column headers
                if (contents.length > 0 && contents[0].length < currentRow.length) {
                    throw Utils_String.format(WITResources.ImporCsvExtraFieldsFoundError, contents.length + 1, contents[0].length, currentRow.length);
                } else if (contents.length > 0 && contents[0].length > currentRow.length) {
                    // Must pad row because of fewer field values than number of column headers
                    for (let i = currentRow.length; i < contents[0].length; i++) {
                        currentRow.push("");
                    }
                }

                // Skip over LF
                if (isCarriageReturnEnding) {
                    i++;
                }

                contents.push(currentRow);
                currentRow = [];
                inToken = false;
                hasQuotes = false;
            } else if (c === '"' && !inToken) {
                // Start of double-quoted value
                inToken = true;
                hasQuotes = true;
            } else if (c === '"' && inToken && hasQuotes && i + 1 < csv.length && csv[i + 1] === '"') {
                // Quote found in middle of double-quoted value
                token += '"';
                i++; // skip next iteration
            } else if (c === '"' && inToken && hasQuotes && i + 1 < csv.length && csv[i + 1] !== '"') {
                // End of double-quoted value
                inToken = false;
                hasQuotes = false;

                // Syntax Error: Ended CSV value too early
                if (
                    csv[i + 1] !== "," &&
                    csv[i + 1] !== this.c_lineFeedChar &&
                    !(csv[i + 1] === this.c_carriageReturnChar && i + 2 < csv.length && csv[i + 2] === this.c_lineFeedChar)
                ) {
                    throw Utils_String.format(WITResources.ImporCsvDoubleQuoteTerminatedEarlyError, contents.length + 1, currentRow.length + 1);
                }
            } else if (c === '"' && inToken && !hasQuotes) {
                // Syntax Error: Random " that is not enclosed in quotes.
                throw Utils_String.format(WITResources.ImporCsvDoubleQuoteNotEnclosedError, contents.length + 1, currentRow.length + 1);
            } else {
                // Otherwise, append character
                inToken = true;
                token += c;
            }
        }

        // Syntax Error: Enclosing value in quotes did not terminate.
        if (hasQuotes) {
            throw Utils_String.format(WITResources.ImporCsvNoTerminatingDoubleQuotesError, contents.length + 1, currentRow.length + 1);
        }

        if (contents.length > this.c_maxWorkItemnsAllowed + 1) {
            throw Utils_String.format(WITResources.ImporCsvMaxRowsExceededError, this.c_maxWorkItemnsAllowed);
        }

        return contents;
    }

    // Handle edge case where CSV can have empty rows / column on the end of the file.
    // Remove the extra rows/columns to streamline validation & reading the contents
    private _trimContents(contents: string[][]): void {
        // trim rows
        let danglingRowIndex = -1;
        for (let i = contents.length - 1; i > 0 && this._isRowEmpty(contents[i]); i--) {
            danglingRowIndex = i;
        }
        if (danglingRowIndex > 0) {
            contents.splice(danglingRowIndex);
        }

        // trim columns
        let danglingColIndex = -1;
        for (let i = contents[0].length - 1; i > 0 && this._isColumnEmpty(i, contents); i--) {
            danglingColIndex = i;
        }
        if (danglingColIndex > 0) {
            contents.forEach(row => row.splice(danglingColIndex));
        }
    }

    private _validateHeaders(contents: string[][]): void {
        if (contents.length === 0) {
            throw WITResources.ImportEmptyDataSet;
        }

        const errorMessages = [];

        // Remove leading and trailing white spaces for column headers
        const headers = (contents[0] = contents[0].map(header => header.trim()));
        const requiredIndicesMap = this._getIndicesMap(headers, this._requiredHeadersMap);

        let emptyColumnHeaderIndices = this._getEmptyColumnHeaderIndices(headers);
        if (emptyColumnHeaderIndices.length > 0) {
            // If an entire column is empty, display separately for clarity
            const emptyColumnIndices = emptyColumnHeaderIndices.filter(index => this._isColumnEmpty(index, contents));
            emptyColumnHeaderIndices = emptyColumnHeaderIndices.filter(index => !this._isColumnEmpty(index, contents));

            if (emptyColumnHeaderIndices.length > 0) {
                errorMessages.push(
                    Utils_String.format(WITResources.ImportEmptyColumnHeaders, emptyColumnHeaderIndices.map(index => index + 1).join(", "))
                );
            }
            if (emptyColumnIndices.length > 0) {
                errorMessages.push(Utils_String.format(WITResources.ImportEmptyColumn, emptyColumnIndices.map(index => index + 1).join(", ")));
            }
        }

        const invalidColumnHeaders = this._getInvalidColumnHeaders(headers);
        if (invalidColumnHeaders.length > 0) {
            errorMessages.push(Utils_String.format(WITResources.ImportInvalidColumnHeaders, invalidColumnHeaders.join(", ")));
        }

        const missingRequiredHeaders = this._getMissingRequiredHeaders(requiredIndicesMap);
        if (missingRequiredHeaders.length > 0) {
            errorMessages.push(Utils_String.format(WITResources.ImportMissingRequiredHeaders, missingRequiredHeaders.join(", ")));
        }

        const duplicateNonRequiredHeaders = this._getDuplicateNonRequiredHeaders(headers);
        if (duplicateNonRequiredHeaders.length > 0) {
            errorMessages.push(Utils_String.format(WITResources.ImportDuplicateNonRequiredHeaders, duplicateNonRequiredHeaders.join(", ")));
        }

        // ie. "Title 1, Title 2, Work Item Type 1, Work Item Type 2"
        const duplicateRequiredHeaders = this._getDuplicateRequiredHeaders(requiredIndicesMap);
        if (duplicateRequiredHeaders.length > 0) {
            errorMessages.push(Utils_String.format(WITResources.ImportDuplicateRequiredHeaders, duplicateRequiredHeaders.join(", ")));
        }

        // ie. "Title 2, Title 1" OR "Title 1, ID, Title 2"
        const nonConsecutiveTreeHeaderIndices = this._getNonConsecutiveTreeHeaderIndices(requiredIndicesMap, headers);
        if (nonConsecutiveTreeHeaderIndices.length > 0) {
            errorMessages.push(
                Utils_String.format(WITResources.ImportNonConsecutiveTreeHeader, nonConsecutiveTreeHeaderIndices.map(index => index + 1).join(", "))
            );
        }

        if (errorMessages.length > 0) {
            throw `${WITResources.ImportErrorMessageTitle}\n${errorMessages.join("\n")}`;
        }
    }

    // Returns a dictionary that maps stripped, lowercased required header names within headersMap to their indices within headers
    private _getIndicesMap(headers: string[], headersMap: { [bareHeader: string]: string }): { [bareHeader: string]: number[] } {
        const result = {};
        const importantHeaders = Object.keys(headersMap);
        importantHeaders.forEach(header => {
            result[header] = []; // Array initialization for tree view import compatibility: "Title 1, Title 2, Title 3, ..." => [ i, i+1, i+2, ... ]
        });

        //Indices pushed in ASC order
        headers.forEach((header, index) => {
            header = this._getFieldNameByColumnName(header).toLocaleLowerCase();
            if (!result[header]) {
                return;
            }

            result[header].push(index);
        });

        return result;
    }

    private _getEmptyColumnHeaderIndices(headers: string[]): number[] {
        const result = [];
        headers.forEach((header, index) => {
            if (header.length === 0) {
                result.push(index);
            }
        });

        return result;
    }

    private _isColumnEmpty(index: number, contents: string[][]): boolean {
        for (let i = 0; i < contents.length; i++) {
            const cell = contents[i][index];
            if (cell && cell.length > 0) {
                return false;
            }
        }
        return true;
    }

    private _isRowEmpty(row: string[]): boolean {
        for (let i = 0; i < row.length; i++) {
            if (row[i] && row[i].length > 0) {
                return false;
            }
        }
        return true;
    }

    private _getInvalidColumnHeaders(headers: string[]): number[] {
        const result = [];
        const headersMap = {};

        // Dedup headers from tree view import
        headers.forEach(header => {
            const key = this._getFieldNameByColumnName(header).toLocaleLowerCase();
            if (key.length > 0 && !headersMap[key]) {
                headersMap[key] = header;
            }
        });

        const distinctHeaders = Object.keys(headersMap);
        distinctHeaders.forEach(header => {
            const field: WITOM.FieldDefinition = this._store.getFieldDefinition(header);
            if (!field) {
                const originalNameStripped = this._getFieldNameByColumnName(headersMap[header]);
                result.push(originalNameStripped); // Push 1st occurrence of original name
            }
        });

        return result;
    }

    // Returns an array of requiredHeaders that are not found in headers
    private _getMissingRequiredHeaders(requiredIndicesMap: { [bareHeader: string]: number[] }): string[] {
        const missingRequiredHeaders = Object.keys(requiredIndicesMap).filter(header => requiredIndicesMap[header].length === 0);

        // Return original header names
        return missingRequiredHeaders.map(header => this._requiredHeadersMap[header]);
    }

    private _getDuplicateNonRequiredHeaders(headers: string[]): string[] {
        const nonRequiredHeadersMap = {};
        headers.forEach(header => {
            const strippedOriginalName = this._getFieldNameByColumnName(header);
            const key = strippedOriginalName.toLocaleLowerCase();
            if (key.length > 0 && !this._requiredHeadersMap[key]) {
                nonRequiredHeadersMap[key] = strippedOriginalName;
            }
        });

        const nonRequiredIndicesMap = this._getIndicesMap(headers, nonRequiredHeadersMap);
        return this._getDuplicateHeaders(nonRequiredIndicesMap, nonRequiredHeadersMap);
    }

    private _getDuplicateRequiredHeaders(requiredIndicesMap: { [bareHeader: string]: number[] }): string[] {
        const result = this._getDuplicateHeaders(requiredIndicesMap, this._requiredHeadersMap);

        // Return if there are multiple duplicates: BOTH 'Title 1', Title 2' and 'Work Item Type 1', 'Work Item Type 2'
        return result.length > 1 ? result : [];
    }

    private _getDuplicateHeaders(indicesMap: { [bareHeader: string]: number[] }, headersMap: { [bareHeader: string]: string }): string[] {
        const result = [];
        const headers = Object.keys(indicesMap);

        headers.forEach(header => {
            const indices = indicesMap[header];
            if (indices.length > 1) {
                result.push(headersMap[header]);
            }
        });

        return result;
    }

    private _getNonConsecutiveTreeHeaderIndices(requiredIndicesMap: { [bareHeader: string]: number[] }, headers: string[]): number[] {
        const indicesArr = Object.keys(requiredIndicesMap).map(header => requiredIndicesMap[header]);
        const result = [];

        for (let i = 0; i < indicesArr.length; i++) {
            const indices = indicesArr[i];

            // Skip singular column headers - we'll let the user use singular 'Title 1' or 'Title 2' and strip the number later
            if (indices.length === 1) {
                continue;
            }

            for (let j = 0; j < indices.length; j++) {
                const index = indices[j];
                const numberSuffix = this._getNumberSuffix(headers[index]);

                // If not consecutive in number or spot, return indices
                if (numberSuffix !== j + 1 || (j > 0 && indices[j] - 1 !== indices[j - 1])) {
                    result.concat(indices.slice()); // Return all instances for now
                }
            }
        }

        return result;
    }

    // Gets a field name by given column name
    // In most cases column name should be same as field name, but in tree view
    // "Export as CSV" exports indented titles as Title 1, Title 2 and etc
    // Hence in such case this function strips the number surfix and returns the real field name
    private _getFieldNameByColumnName(columnName: string): string {
        const field: WITOM.FieldDefinition = this._store.getFieldDefinition(columnName);
        if (field) {
            return columnName;
        }

        let result = columnName;
        const parts = columnName.split(" ");

        if (parts.length > 0 && Number(parts[parts.length - 1])) {
            result = parts.slice(0, -1).join(" ");
        }

        return result;
    }

    // Returns the number suffix if it exists or -1 otherwise: "Title 1" -> 1
    private _getNumberSuffix(header: string): number {
        let result = -1;
        const parts = header.split(" ");

        if (parts.length > 0 && Number(parts[parts.length - 1])) {
            result = Number(parts[parts.length - 1]);
        }

        return result;
    }

    // Rely on the fact that occurences of 'Title 1, Title 2, Title 3' signify tree view import
    private _isTreeViewImport(requiredIndicesMap: { [bareHeader: string]: number[] }) {
        const indices = Object.keys(requiredIndicesMap)
            .map(header => requiredIndicesMap[header])
            .filter(indices => indices.length > 1);
        return indices.length > 0;
    }

    // Assumes column headers are validated already
    private _validateRequiredFields(contents: string[][]): void {
        if (contents.length === 0) {
            return;
        }

        const errorMessages = [];
        const workItemTypesMap = this._workItemTypesMap;
        const headers = contents[0];
        const idIndex = this._getHeaderIndex(headers, this._idHeader);
        const requiredIndicesMap = this._getIndicesMap(headers, this._requiredHeadersMap);
        const idsMap = {};

        let maxIndentationAllowed = 1;

        for (let i = 1; i < contents.length; i++) {
            const csvRow = contents[i].map(field => field.trim());

            // Cache ids and record their row indices
            const id = idIndex >= 0 && csvRow[idIndex].length > 0 ? Number(csvRow[idIndex]) : null;
            if (id && id !== 0) {
                idsMap[id] = idsMap[id] || [];
                idsMap[id].push(i);
            } else if (id && id === 0) {
                errorMessages.push(Utils_String.format(WITResources.ImporCsvInvalidWorkItemIdError, this._idHeader, i + 1, idIndex + 1));
            } else if (isNaN(id)) {
                errorMessages.push(Utils_String.format(WITResources.ImporCsvNonIntegerWorkItemIdError, this._idHeader, i + 1, idIndex + 1));
            }

            // Validate field values that belong under the required headers
            const requiredHeaders = Object.keys(requiredIndicesMap);
            requiredHeaders.forEach(requiredHeader => {
                const indices = requiredIndicesMap[requiredHeader];
                let numFieldsFound = 0;
                let currentIndentation = 0;
                let fieldValue: string = null;

                // Only one field value must be non-empty
                indices.forEach((index, indentation) => {
                    if (csvRow[index].length > 0) {
                        currentIndentation = indentation;
                        numFieldsFound++;
                        fieldValue = csvRow[index];
                    }
                });

                if (numFieldsFound === 0) {
                    errorMessages.push(
                        Utils_String.format(WITResources.ImporCsvEmptyFieldValuesError, i + 1, indices.map(index => index + 1).join(", "))
                    );
                } else if (numFieldsFound > 1) {
                    errorMessages.push(
                        Utils_String.format(WITResources.ImporCsvExcessFieldValuesError, i + 1, indices.map(index => index + 1).join(", "))
                    );
                } else if (numFieldsFound === 1 && indices.length > 1) {
                    // Validate for proper tree structure
                    if (currentIndentation > maxIndentationAllowed) {
                        errorMessages.push(
                            Utils_String.format(WITResources.ImporCsvIncorrectTreeStructureError, i + 1, indices.map(index => index + 1).join(", "))
                        );
                    } else {
                        maxIndentationAllowed = currentIndentation + 1;
                    }
                } else if (
                    numFieldsFound === 1 &&
                    requiredHeader === this._workItemTypeHeader.toLocaleLowerCase() &&
                    Object.keys(workItemTypesMap).length > 0
                ) {
                    // Validate for Work Item Type
                    if (!workItemTypesMap[fieldValue.toLocaleLowerCase()]) {
                        errorMessages.push(
                            Utils_String.format(
                                WITResources.ImporCsvInvalidFieldValueError,
                                fieldValue,
                                this._workItemTypeHeader,
                                i + 1,
                                indices.map(index => index + 1).join(", ")
                            )
                        );
                    }
                }
            });
        }

        // Log duplicate ids
        const ids = Object.keys(idsMap);
        ids.forEach(id => {
            const rows = idsMap[id];
            if (rows.length > 1) {
                errorMessages.push(
                    Utils_String.format(
                        WITResources.ImporCsvDuplicateWorkItemIdColumnError,
                        this._idHeader,
                        id,
                        rows.map((index: number) => index + 1).join(", ")
                    )
                );
            }
        });

        if (errorMessages.length > 0) {
            throw Utils_String.format(WITResources.ImporCsvErrorsFound, errorMessages.join("\n"));
        }
    }

    private _getHeaderIndex(headers: string[], headerName: string): number {
        headerName = headerName.toLocaleLowerCase();
        for (let i = 0; i < headers.length; i++) {
            const header = headers[i].toLocaleLowerCase();
            if (header === headerName) {
                return i;
            }
        }

        return -1;
    }

    // Gets a FieldDefinition by column name.
    // If column name is not a real field name (e.g. Title 1),
    // it strips number suffix and use that to fetch field definition.
    private _getFieldDefinitionByColumnName(columnName: string): WITOM.FieldDefinition {
        const fieldName = this._getFieldNameByColumnName(columnName);
        return this._store.getFieldDefinition(fieldName);
    }

    private _getCSVImportData(contents: string[][]): IWorkItemsImportData {
        const result: IWorkItemsImportData = {
            columns: [],
            idsMap: {},
            fieldValues: [],
            targetIds: [],
            sourceIds: [],
            isTreeQuery: false
        };

        if (contents.length === 0) {
            return result;
        }

        result.columns = contents[0].slice();

        const requiredIndicesMap = this._getIndicesMap(result.columns, this._requiredHeadersMap);
        const treeIndicesArr = Object.keys(requiredIndicesMap)
            .map(header => requiredIndicesMap[header])
            .filter(indices => indices.length > 1);

        result.fieldValues = new Array(contents.length - 1);
        result.sourceIds = new Array(contents.length - 1);
        result.targetIds = new Array(contents.length - 1);
        result.isTreeQuery = this._isTreeViewImport(requiredIndicesMap);

        const treeIndices = treeIndicesArr.length > 0 ? treeIndicesArr[0] : []; // At most 1 instance, sorted in ASC order
        const idIndex = this._getHeaderIndex(result.columns, this._idHeader); // -1 if ID column does not exist
        const indentationMap = {}; // Indentation -> ID
        let tempId = -1; // Assigned to new work items

        // Remove redundant tree column headers: 'Title 1, Title 2, Title 3' -> 'Title'
        for (let i = treeIndices.length - 1; i >= 0; i--) {
            const index = treeIndices[i];
            if (i > 0) {
                result.columns.splice(index, 1);
            } else {
                result.columns[index] = this._getFieldNameByColumnName(result.columns[index]);
            }
        }

        result.columns = result.columns.map(column => this._getFieldDefinitionByColumnName(column).referenceName);

        // Package CSV data into ICSVImportData
        for (let i = 1; i < contents.length; i++) {
            const row: string[] = (result.fieldValues[i - 1] = contents[i].slice().map(fieldValue => fieldValue.trim()));
            let indentation = 0;

            // Assign negative ids to new work items
            let id = idIndex >= 0 && row[idIndex].length > 0 ? Number(row[idIndex]) : tempId--;

            // Flatten Tree View CSV: Remove empty fields correspnding to tree view indentation while recording the id's indentation level
            for (let j = treeIndices.length - 1; j >= 0; j--) {
                const index = treeIndices[j];
                if (row[index].length === 0) {
                    row.splice(index, 1);
                } else {
                    indentation = j;
                }
            }

            // Force insert ID column value
            if (idIndex < 0) {
                row.unshift("");
            }

            indentationMap[indentation] = id;

            result.targetIds[i - 1] = id;
            result.idsMap[id] = i - 1;
            result.sourceIds[i - 1] = indentation === 0 ? 0 : indentationMap[indentation - 1];
        }

        // Force insert ID column, if it's not present, because it doesn't make sense not to show the new work item IDs
        if (idIndex < 0) {
            result.columns.unshift(WITConstants.CoreFieldRefNames.Id);
        }

        return result;
    }

    // Asynchronously load work items
    private async _loadWorkItemsAndSetFieldValues(importData: IWorkItemsImportData): Promise<void> {
        const ids = importData.targetIds;
        const columns = importData.columns;
        const fieldValuesArr = importData.fieldValues;
        const idsMap = importData.idsMap;
        const sourceIds = importData.sourceIds;
        const workItemTypesMap = this._workItemTypesMap;

        const wiManager: WorkItemManager = WorkItemManager.get(this._store);
        const idIndex = columns.indexOf(WITConstants.CoreFieldRefNames.Id);
        const typeIndex = columns.indexOf(WITConstants.CoreFieldRefNames.WorkItemType);
        const unsolvedIdentities: { [key: string]: WITOM.Field[] } = {};

        wiManager.resetCache();

        const separateNewAndExistingWorkItems = (currentIds: number[], newIds: number[], existingIds: number[], workItemTypeNames: string[]) => {
            for (let i = 0; i < currentIds.length; i++) {
                const id = currentIds[i];
                if (id < 0) {
                    const fieldValues = fieldValuesArr[idsMap[id]];
                    workItemTypeNames.push(fieldValues[typeIndex]);
                }

                const idsArr = id > 0 ? existingIds : newIds;
                idsArr.push(id);
            }
        };

        const getExistingWorkItems = (existingIds: number[]): Promise<void> => {
            return new Promise((resolve, reject) => {
                wiManager.beginGetWorkItems(
                    existingIds,
                    (workItems: WITOM.WorkItem[]) => {
                        if (this._disposed) {
                            resolve();
                        }
                        workItems.forEach((workItem) => {
                            const id = workItem.id;
                            const fieldValues = fieldValuesArr[idsMap[id]];
                            try {
                                applyFieldValueChanges(workItem, fieldValues, true);
                            } catch (error) {
                                reject(error);
                            }
                        });
                        resolve();
                    },
                    (error) => reject({ message: error.message, errorType: "beginGetWorkItems" })
                );
            });
        };

        const createNewWorkItems = (newIds: number[], workItemTypeNames: string[]): void => {
            for (let i = 0; i < workItemTypeNames.length; i++) {
                const id = newIds[i];
                const typeName: string = workItemTypeNames[i];
                const workItemType: WITOM.WorkItemType = workItemTypesMap[typeName.toLocaleLowerCase()];
                const fieldValues = fieldValuesArr[idsMap[id]];
                const workItemData: IWorkItemData = {
                    projectId: null,
                    fields: null,
                    revisions: null,
                    tags: null,
                    files: null,
                    referencedPersons: null,
                    tempId: id, // This is fine as long we're overwriting negative ids for all new work items with our own import tempIds numbering system
                    loadTime: null,
                    currentExtensions: null,
                    referencedNodes: null
                };

                const workItem: WITOM.WorkItem = wiManager.createWorkItem(workItemType, workItemData);
                applyFieldValueChanges(workItem, fieldValues);
            }
        };

        const applyFieldValueChanges = (workItem: WITOM.WorkItem, fieldValues: string[], existingWorkItem: boolean = false): void => {
            // Change field values
            if (fieldValues && fieldValues.length > 0) {
                fieldValues.forEach((fieldValue, index) => {
                    if (index === idIndex) {
                        // || index === typeIndex) {
                        return;
                    }

                    const fieldReferenceName = columns[index];
                    const field: WITOM.Field = workItem.getField(fieldReferenceName);
                    if (field) {
                        if (field.fieldDefinition.isIdentity) {
                            const identityDisplayName = field.getIdentityValue() ? field.getIdentityValue().displayName : "";
                            if (!Utils_String.equals(identityDisplayName, fieldValue, true)) {

                                const options: IFieldSetValueOptions = {
                                    fireIdentityEagerValidation: true
                                };

                                // set the imported displayName first
                                field.setValue(fieldValue, options);

                                // collect unsolved identities with associated fields
                                const key = fieldValue.toLocaleLowerCase();
                                if (!unsolvedIdentities[key]) {
                                    unsolvedIdentities[key] = [field];
                                } else {
                                    unsolvedIdentities[key].push(field);
                                }
                            }
                        } else if (field.fieldDefinition.id === WITConstants.CoreField.WorkItemType) {
                            if (existingWorkItem) {
                                const workItemType: WITOM.WorkItemType = workItemTypesMap[fieldValue.toLocaleLowerCase()];
                                workItem.changeWorkItemType(workItemType);
                            } else {
                                field.setValue(fieldValue);
                            }
                        } else {
                            if (field.isReadOnly()) {
                                const existingValue = field.getValue(true /* original */);
                                if (!Utils_String.equals(existingValue, fieldValue, true)) {
                                    // Readonly field's value updated, throw error and bail out
                                    throw Utils_String.format(WITResources.ImporCsvReadonlyFieldModifiedError, field.fieldDefinition.name);
                                }
                            }
                            field.setValue(fieldValue);
                        }
                    }
                });
            }

            this._updatePercentComplete(1 / ids.length, 1, 1);
        };

        const addLinks = (ids: number[]): void => {
            for (let i = 0; i < ids.length; i++) {
                const sourceId = sourceIds[idsMap[ids[i]]];
                if (sourceId !== 0) {
                    const workItem = wiManager.getWorkItem(ids[i]);
                    const parentWorkItem = wiManager.getWorkItem(sourceId);

                    // Always create a link that is directed towards an existing work item, unless both work items are new, then we always create a 'Parent' link
                    if (sourceId < 0 && workItem.id > 0 && parentWorkItem) {
                        parentWorkItem.addLink(WITOM.WorkItemLink.create(parentWorkItem, LinkTypeName.Child_Link_Name, workItem.id, ""));
                    } else {
                        workItem.addLink(WITOM.WorkItemLink.create(workItem, LinkTypeName.Parent_Link_Name, sourceId, ""));
                    }
                }
            }
        };

        const newIds: number[] = [];
        const existingIds: number[] = [];
        const workItemTypeNames: string[] = [];
        const existingWorkItemsPromises: Promise<void>[] = [];

        // Separate the work items upfront
        separateNewAndExistingWorkItems(ids, newIds, existingIds, workItemTypeNames);

        // Asynchronously fetch 200 WI from the server
        for (let i = 0; i < existingIds.length; i += PageSizes.QUERY) {
            if (this._disposed) {
                return;
            }
            const subsetIds = existingIds.slice(i, i + PageSizes.QUERY);
            existingWorkItemsPromises.push(getExistingWorkItems(subsetIds));
        }

        createNewWorkItems(newIds, workItemTypeNames);
        await Promise.all(existingWorkItemsPromises);

        await this._autoResolveIdentityFields(unsolvedIdentities);

        if (this._disposed) {
            return;
        }

        addLinks(ids);
    }

    private async _autoResolveIdentityFields(unresolvedIdentityMap: { [key: string]: WITOM.Field[] }): Promise<void> {
        const keys = Object.keys(unresolvedIdentityMap);
        if (keys.length == 0) {
            return Promise.resolve();
        }

        const IdentityPageSize = 50; // batch size used for getIdentities call
        const allPromises = [];

        for (let i = 0; i < keys.length; i += IdentityPageSize) {
            const identityNames = keys.slice(i, i + IdentityPageSize);
            allPromises.push(this._beginResolveIdentityFields(unresolvedIdentityMap, identityNames));
        }

        await Promise.all(allPromises);

        return Promise.resolve();
    }

    private async _beginResolveIdentityFields(unresolvedIdentityMap: { [key: string]: WITOM.Field[] }, keys: string[]): Promise<void> {
        const resolvedIdentityMap: { [key: string]: string } = {};
        const allIdentities = keys.join(";");

        // try to resolve the identity by name
        const operationScope: IdentitiesPickerServices.IOperationScope = {
            Source: true,
            IMS: true
        };

        const identityType: IdentitiesPickerServices.IEntityType = {
            User: true,
            Group: true
        };

        const promise = new Promise<void>((resolve, reject) => {
            const result = this._identityService.getIdentities(allIdentities, operationScope, identityType);
            if (result) {
                const identityPromises = Object.keys(result).map(key => result[key]);
                Promise.all(identityPromises)
                    .then(queryResult => {
                        queryResult.forEach((model: QueryTokenResultModel) => {
                            if (model.identities.length === 1) {
                                const key = model.queryToken.toLocaleLowerCase(); // server should return lower cased version, but make sure it is to match unresolvedIdentityMap
                                resolvedIdentityMap[key] = model.identities[0].displayName; // identity is considered to be resolved only if there was one match
                            }
                        });

                        Object.keys(resolvedIdentityMap).forEach(identity => {
                            // set value to resolved identity name for fields associated with the imported identity value
                            unresolvedIdentityMap[identity].forEach(field => field.setValue(resolvedIdentityMap[identity]));
                        });

                        resolve();
                    })
                    .catch(reject);
            } else {
                resolve();
            }
        });

        return promise;
    }
}
